import React from 'react';
import { BookmarkCheck, BookmarkPlus } from 'lucide-react';
import { Button } from '../ui/button';

const TestHeader = ({
  currentQuestionIndex,
  totalQuestions,
  questionLabel,
  score,
  isBookmarked,
  isBookmarkLoading,
  onToggleBookmark,
  rcContext,
  headerAddon
}) => {
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;
  const isRcSubQuestion = Boolean(rcContext);

  const position = `${currentQuestionIndex + 1} of ${totalQuestions}`;

  // The paper's name for the question - 2b, 3.2a - as the tray and the printed paper call it,
  // then how far through the list the student is. Without a label, the position alone.
  const getMobileQuestionLabel = () => {
    if (questionLabel) return `${questionLabel} · ${position}`;
    if (isRcSubQuestion) {
      return `${currentQuestionIndex + 1}.${rcContext.subIndex + 1} of ${totalQuestions}`;
    }
    return position;
  };

  const bookmarkButton = onToggleBookmark ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onToggleBookmark}
      disabled={isBookmarkLoading}
      className={`h-auto gap-1.5 px-2 py-1 md:gap-2 md:px-3 md:py-1.5 ${
        isBookmarked
          ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-700'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      {isBookmarked ? (
        <>
          <BookmarkCheck className="h-3.5 w-3.5 md:h-4 md:w-4" />
          <span className="text-xs font-medium md:text-sm">Bookmarked</span>
        </>
      ) : (
        <>
          <BookmarkPlus className="h-3.5 w-3.5 md:h-4 md:w-4" />
          <span className="text-xs font-medium md:text-sm">Bookmark</span>
        </>
      )}
    </Button>
  ) : null;

  return (
    <div className="bg-white px-[14px] pt-3 shadow-sm md:py-3">
      {/* Mobile */}
      <div className="md:hidden">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Question {getMobileQuestionLabel()}</span>
            {isRcSubQuestion && (
              <>
                <div className="h-3 w-px bg-border" />
                <span className="text-xs font-medium text-primary">
                  Part {rcContext.subIndex + 1}/{rcContext.totalSub}
                </span>
              </>
            )}
            {!isRcSubQuestion && score != null && (
              <>
                <div className="h-3 w-px bg-border" />
                <span className="text-sm text-muted-foreground">{score} pts</span>
              </>
            )}
          </div>
          {headerAddon || bookmarkButton}
        </div>
        <div className="cw-progress-bar">
          <div className="cw-progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:block">
        <div className="mb-1.5 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-foreground">
                Question {questionLabel || currentQuestionIndex + 1}
              </span>
              <span className="text-sm text-muted-foreground">
                {questionLabel ? `· ${position}` : `of ${totalQuestions}`}
              </span>
            </div>

            {isRcSubQuestion && (
              <>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-primary">Part {rcContext.subIndex + 1}</span>
                  <span className="text-sm text-muted-foreground">of {rcContext.totalSub}</span>
                </div>
              </>
            )}

            {score != null && (
              <>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium text-foreground">{score}</span>
                  <span className="text-sm text-muted-foreground">pts</span>
                </div>
              </>
            )}

            {headerAddon ? (
              <>
                <div className="h-4 w-px bg-border" />
                {headerAddon}
              </>
            ) : (
              bookmarkButton && (
                <>
                  <div className="h-4 w-px bg-border" />
                  {bookmarkButton}
                </>
              )
            )}
          </div>
        </div>
        <div className="cw-progress-bar">
          <div className="cw-progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
};

export default TestHeader;
